import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantTrades } from '../../hooks/useTenantTrades';
import { FormService } from '../../services/FormService';
import { useTenant } from '../../contexts/TenantContext';
import { useTheme } from '../../contexts/ThemeContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiSearch,
  FiFileText,
  FiPlus,
  FiSettings,
  FiGrid,
  FiMoon,
  FiSun,
  FiMonitor,
  FiFolder,
  FiUsers,
  FiDatabase,
  FiLayers,
  FiArrowRight,
  FiCornerDownLeft,
  FiMapPin
} = FiIcons;

/**
 * CommandPalette (Cmd+K / Ctrl+K)
 * Global fast navigation & quick action search bar
 */
export const CommandPalette = ({ isOpen, onClose, onSelectCertificate }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentCertificates, setRecentCertificates] = useState([]);
  const navigate = useNavigate();
  const { organizations, activeTenant, switchTenant } = useTenant();
  const { theme, setTheme } = useTheme();
  const { allowedForms } = useTenantTrades();
  const inputRef = useRef(null);

  // Trade-gated: only certificates matching the tenant's enabled trades
  const availableForms = allowedForms;

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
      loadRecent();
    }
  }, [isOpen]);

  const loadRecent = async () => {
    try {
      const recents = await FormService.getRecentForms(5);
      setRecentCertificates(recents || []);
    } catch (e) {
      console.warn('Failed to load recents for CommandPalette:', e);
    }
  };

  // Build searchable items
  const allActions = [
    // Navigation items
    {
      id: 'nav-dashboard',
      category: 'Navigation',
      title: 'Go to Dashboard',
      subtitle: 'Overview, statistics and quick actions',
      icon: FiGrid,
      action: () => navigate('/dashboard')
    },
    {
      id: 'nav-library',
      category: 'Navigation',
      title: 'Certificate Library',
      subtitle: 'Browse all created certificates and records',
      icon: FiFolder,
      action: () => navigate('/forms')
    },
    {
      id: 'nav-customers',
      category: 'Navigation',
      title: 'Customer Directory',
      subtitle: 'Homeowners, landlords, agents & contractors',
      icon: FiUsers,
      action: () => navigate('/customers')
    },
    {
      id: 'nav-properties',
      category: 'Navigation',
      title: 'Property & Site Registry',
      subtitle: 'Managed sites, appliances & histories',
      icon: FiMapPin,
      action: () => navigate('/properties')
    },
    {
      id: 'nav-templates',
      category: 'Navigation',
      title: 'Templates & Presets',
      subtitle: 'Manage reusable certificate templates',
      icon: FiLayers,
      action: () => navigate('/templates')
    },
    {
      id: 'nav-backup',
      category: 'Navigation',
      title: 'Data & Cloud Backups',
      subtitle: 'Export / import and offline storage',
      icon: FiDatabase,
      action: () => navigate('/backup')
    },
    {
      id: 'nav-settings',
      category: 'Navigation',
      title: 'Organization Settings',
      subtitle: 'Branding, accreditations & preferences',
      icon: FiSettings,
      action: () => navigate('/settings')
    },

    // Theme toggles
    {
      id: 'theme-light',
      category: 'Preferences',
      title: 'Switch to Light Theme',
      subtitle: 'Standard clean high-contrast mode',
      icon: FiSun,
      action: () => setTheme('light')
    },
    {
      id: 'theme-dark',
      category: 'Preferences',
      title: 'Switch to Dark Theme',
      subtitle: 'Low-light tactical interface',
      icon: FiMoon,
      action: () => setTheme('dark')
    },
    {
      id: 'theme-system',
      category: 'Preferences',
      title: 'Use System Theme',
      subtitle: 'Follow OS appearance settings',
      icon: FiMonitor,
      action: () => setTheme('system')
    },

    // Create New Form Actions
    ...availableForms.map((form) => ({
      id: `new-${form.id}`,
      category: 'New Certificate',
      title: `Create ${form.name}`,
      subtitle: `${form.category} • ${form.description}`,
      icon: FiPlus,
      badge: form.icon,
      action: () => navigate(`/forms/${form.id}`)
    })),

    // Switch Organization Actions
    ...organizations.map((org) => ({
      id: `org-${org.id}`,
      category: 'Switch Organization',
      title: `Switch to ${org.name}`,
      subtitle: `${org.plan} • Active: ${org.id === activeTenant.id ? 'Yes' : 'No'}`,
      icon: FiUsers,
      action: () => switchTenant(org.id)
    })),

    // Recent Certificates
    ...recentCertificates.map((item) => {
      const config = availableForms.find((f) => f.id === item.formId);
      return {
        id: `recent-${item.id}`,
        category: 'Recent Certificate',
        title: item.instanceId || `${config?.name || 'Certificate'} #${item.id}`,
        subtitle: `${config?.name || item.formId} • Status: ${item.status}`,
        icon: FiFileText,
        action: () => {
          if (onSelectCertificate) {
            onSelectCertificate(item);
          } else {
            navigate(`/forms/${item.formId}/${item.id}`);
          }
        }
      };
    })
  ];

  const filteredActions = allActions.filter((item) => {
    const q = query.toLowerCase();
    return (
      item.title.toLowerCase().includes(q) ||
      (item.subtitle && item.subtitle.toLowerCase().includes(q)) ||
      item.category.toLowerCase().includes(q)
    );
  });

  // Keyboard navigation inside list
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % Math.max(1, filteredActions.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + filteredActions.length) % Math.max(1, filteredActions.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selected = filteredActions[selectedIndex];
      if (selected) {
        selected.action();
        onClose();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4 bg-slate-950/60 backdrop-blur-sm animate-in fade-in duration-150">
      <div
        className="w-full max-w-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[75vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input Bar */}
        <div className="flex items-center px-4 py-3.5 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
          <SafeIcon icon={FiSearch} className="w-5 h-5 text-slate-400 mr-3 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Type a command, certificate type, or search action..."
            className="w-full bg-transparent border-none text-slate-900 dark:text-slate-100 placeholder-slate-400 text-sm focus:outline-none"
          />
          <span className="kbd-shortcut">ESC</span>
        </div>

        {/* Results List */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {filteredActions.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <SafeIcon icon={FiSearch} className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm font-medium">No results found for &ldquo;{query}&rdquo;</p>
              <p className="text-xs text-slate-400 mt-1">Try searching for &apos;gas&apos;, &apos;settings&apos;, &apos;theme&apos;, or a certificate name</p>
            </div>
          ) : (
            filteredActions.map((item, idx) => {
              const isSelected = idx === selectedIndex;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    item.action();
                    onClose();
                  }}
                  onMouseEnter={() => setSelectedIndex(idx)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-left transition-all ${
                    isSelected
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isSelected
                          ? 'bg-white/20 text-white'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
                      }`}
                    >
                      {item.badge ? (
                        <span className="text-base">{item.badge}</span>
                      ) : (
                        <SafeIcon icon={item.icon} className="w-4 h-4" />
                      )}
                    </div>
                    <div className="min-w-0 truncate">
                      <p className={`text-xs font-semibold truncate ${isSelected ? 'text-white' : 'text-slate-900 dark:text-slate-100'}`}>
                        {item.title}
                      </p>
                      <p className={`text-[11px] truncate ${isSelected ? 'text-blue-100' : 'text-slate-500 dark:text-slate-400'}`}>
                        {item.subtitle}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    <span
                      className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded ${
                        isSelected
                          ? 'bg-blue-700 text-blue-100'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                      }`}
                    >
                      {item.category}
                    </span>
                    {isSelected && <SafeIcon icon={FiCornerDownLeft} className="w-3.5 h-3.5 text-blue-200" />}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Footer info */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-slate-200 dark:border-slate-800 text-[11px] text-slate-400 bg-slate-50 dark:bg-slate-900/80">
          <div className="flex items-center gap-3">
            <span>Navigation: <span className="kbd-shortcut">↑</span> <span className="kbd-shortcut">↓</span></span>
            <span>Select: <span className="kbd-shortcut">↵</span></span>
            <span>Close: <span className="kbd-shortcut">ESC</span></span>
          </div>
          <span>Active Org: <strong className="text-slate-600 dark:text-slate-300">{activeTenant.name}</strong></span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
